/**
 * The words Hermes Chat uses about itself.
 *
 * Every string a user reads on the chat surface is here, for one reason: the
 * transcript is the one screen where the model's INTERNAL vocabulary — tool
 * names, drop reasons, action kinds — is only a render away from the page. A
 * transcript row that said `search_timeline` or `guard_failed` would be exactly
 * the developer jargon the 2026-08-29 sweep took out of the rest of the product.
 *
 * Pure data and no React so the mapping is unit-testable. The two records keyed
 * on a contract enum are typed `Record<ChatDropReason, string>` /
 * `Record<ChatActionKind, string>`, so adding a case to `@crm/contracts` fails
 * the typecheck HERE until someone writes the sentence a person will read.
 */

import type { CHAT_ACTION_KINDS, CHAT_DROP_REASONS } from '@crm/contracts';

type ChatDropReason = (typeof CHAT_DROP_REASONS)[number];
type ChatActionKind = (typeof CHAT_ACTION_KINDS)[number];

/**
 * The tool names, mirrored from `CHAT_TOOL_NAMES` in `@crm/intelligence`.
 *
 * Copied rather than imported on purpose: `@crm/intelligence` is a SERVER
 * package (model clients, the price book, the ledger, a Postgres-backed
 * runtime), and importing it into a browser bundle to reach nine string
 * literals would drag all of that into the SPA. The list is not on the wire in
 * any other form — `chatMessageSchema.toolName` is a bare `z.string()`, because
 * a transcript recorded under an older tool set must still deserialise.
 *
 * `toolLabel` therefore treats an unknown name as a real possibility rather than
 * an impossibility, and degrades to a vague-but-true line instead of printing
 * the raw identifier.
 */
export const CHAT_TOOL_NAMES = [
  'search_contacts',
  'get_contact_dossier',
  'search_timeline',
  'list_commitments',
  'list_follow_ups',
  'create_task',
  'schedule_follow_up',
  'draft_message',
  'snooze_contact',
] as const;

export type ChatToolName = (typeof CHAT_TOOL_NAMES)[number];

/**
 * What a tool call DID, in the past tense, from the user's point of view.
 *
 * Deliberately not "Called search_timeline": the disclosure exists so a person
 * can judge whether the answer rests on the right evidence, and a function name
 * does not help them do that.
 */
export const TOOL_LABELS: Readonly<Record<ChatToolName, string>> = {
  search_contacts: 'Searched your contacts',
  get_contact_dossier: 'Opened a contact’s full profile',
  search_timeline: 'Searched your timeline',
  list_commitments: 'Checked promises and asks',
  list_follow_ups: 'Checked your follow-up queue',
  create_task: 'Added a task to your queue',
  snooze_contact: 'Snoozed a contact',
  schedule_follow_up: 'Drafted a follow-up for you to approve',
  draft_message: 'Wrote a message for you to approve',
};

export function isKnownTool(name: string): name is ChatToolName {
  return (CHAT_TOOL_NAMES as readonly string[]).includes(name);
}

export function toolLabel(name: string): string {
  return isKnownTool(name) ? TOOL_LABELS[name] : 'Looked something up';
}

/**
 * Why a turn produced no reply.
 *
 * `hermes.chat.send` already returns a `message` written server-side, and the UI
 * prefers it — these are for a transcript read back through `chat.history`,
 * which carries the reason code and no prose. Keeping the two in the same voice
 * matters: the same failure must not read one way live and another on reload.
 */
export const DROP_REASON_TEXT: Readonly<Record<ChatDropReason, string>> = {
  guard_failed:
    'Hermes could not answer that without inventing something, so the reply was discarded rather than shown.',
  empty_reply: 'Hermes did not manage to put an answer together.',
  model_error: 'The assistant could not be reached for that message.',
  not_configured: 'Hermes Chat is not switched on for this workspace yet.',
  budget_exhausted: 'This workspace has reached its monthly AI budget. An owner can raise the cap.',
  input_too_large: 'That conversation grew too long for one turn. Start a new chat.',
  closed_weight_blocked:
    'Hermes Chat is set to a model this workspace does not allow for private information.',
  queued: 'This workspace has reached its monthly AI budget. An owner can raise the cap.',
};

export function dropReasonText(reason: string): string {
  return (
    (DROP_REASON_TEXT as Record<string, string | undefined>)[reason] ??
    'That message did not produce a reply.'
  );
}

/** The heading on a proposal card. */
export const ACTION_TITLES: Readonly<Record<ChatActionKind, string>> = {
  schedule_follow_up: 'Schedule a follow-up',
  draft_message: 'Save a draft message',
};

/** What confirming will actually do — shown under the heading, before the buttons. */
export const ACTION_EFFECTS: Readonly<Record<ChatActionKind, string>> = {
  schedule_follow_up:
    'Adds this to your follow-up queue so it appears on Today. Nothing is sent to anyone.',
  draft_message:
    'Saves the message as a draft you can edit and send yourself. Nothing is sent until you tap send.',
};

export function actionTitle(kind: string): string {
  return (ACTION_TITLES as Record<string, string | undefined>)[kind] ?? 'Hermes wants to do this';
}

export function actionEffect(kind: string): string {
  return (
    (ACTION_EFFECTS as Record<string, string | undefined>)[kind] ??
    'Nothing happens until you approve it.'
  );
}
